import { useEffect, useState, useCallback } from "react";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import api, { formatApiError } from "@/lib/api";
import { NotesPanel, ReminderPanel } from "@/components/Communication";
import { toast } from "sonner";
import {
  Hand, CheckCircle2, Lock, Mail, Phone, MapPin, Building2, Calendar,
  Activity, IdCard, Tag, Hash, Pencil, Check, X,
} from "lucide-react";

const STATUS_COLORS = { unclaimed: "#A8A29E", claimed: "#EAB308", contacted: "#4A5D23" };

const ACTION_LABELS = {
  created: "Created", viewed: "Viewed", edited: "Edited", claimed: "Claimed",
  marked_contacted: "Marked Contacted", reassigned: "Reassigned",
  unassigned: "Unassigned", import_committed: "Import Committed",
  reminder_set: "Reminder Set",
};

const ACTION_COLORS = {
  created: "#57534E", viewed: "#A8A29E", edited: "#4A6FA5", claimed: "#EAB308",
  marked_contacted: "#4A5D23", reassigned: "#B85C38", unassigned: "#A8A29E",
  import_committed: "#1C1917", reminder_set: "#B85C38",
};

function formatTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

export default function ContactSheet({
  contact: contactProp, users, currentUser, onClose, onClaim, onContacted, onReassign, onUpdated,
}) {
  const [contact, setContact] = useState(contactProp);
  const [activity, setActivity] = useState([]);
  const [loadingActivity, setLoadingActivity] = useState(false);
  const [reassignTo, setReassignTo] = useState(contactProp.assigned_to || "unassigned");
  const [editingCount, setEditingCount] = useState(0);

  const isAdmin = currentUser?.role === "admin";
  const locked = contact.status === "contacted";
  const mine = contact.assigned_to === currentUser?.id;
  const statusColor = STATUS_COLORS[contact.status] || "#A8A29E";
  const canEnrich =
    !locked && (isAdmin || !contact.assigned_to || mine);

  useEffect(() => {
    setContact(contactProp);
    setReassignTo(contactProp.assigned_to || "unassigned");
  }, [contactProp]);

  const loadActivity = useCallback(async () => {
    setLoadingActivity(true);
    try {
      const { data } = await api.get(`/contacts/${contact.id}/activity`);
      setActivity(data);
    } catch {
      setActivity([]);
    } finally {
      setLoadingActivity(false);
    }
  }, [contact.id]);

  useEffect(() => { loadActivity(); }, [loadActivity]);

  const saveField = async (field, value) => {
    const prev = contact[field] || null;
    const next = value || null;
    if (prev === next) return; // no-op guard on client
    try {
      const { data } = await api.patch(`/contacts/${contact.id}`, { [field]: next });
      setContact(data);
      const label = field === "email" ? "Email" : "Phone";
      toast.success(next ? `${label} updated` : `${label} cleared`);
      loadActivity();
      onUpdated?.();
    } catch (e) {
      const msg = formatApiError(e.response?.data?.detail) || "Failed to update";
      toast.error(msg);
      throw e;
    }
  };

  return (
    <Sheet open onOpenChange={(o) => !o && onClose()}>
      <SheetContent
        className="w-full sm:max-w-xl rounded-none border-l-stone-300 bg-white p-0 overflow-y-auto"
        data-testid="contact-sheet"
        onEscapeKeyDown={(e) => { if (editingCount > 0) e.preventDefault(); }}
        onPointerDownOutside={(e) => { if (editingCount > 0) e.preventDefault(); }}
      >
        <SheetHeader className="px-6 pt-6 pb-4 border-b border-stone-200">
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            <span
              className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-mono uppercase tracking-wider"
              style={{ backgroundColor: `${statusColor}1A`, color: statusColor }}
            >
              <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: statusColor }} />
              {contact.status}
            </span>
            {contact.license_code && (
              <Badge className="rounded-sm font-mono text-[10px] uppercase tracking-wider border bg-transparent text-[#4A6FA5] border-[#4A6FA5]/40">
                {contact.license_code}
              </Badge>
            )}
            {locked && (
              <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider text-[#4A5D23]">
                <Lock className="h-3 w-3" /> Locked
              </span>
            )}
          </div>
          <SheetTitle className="font-display text-2xl font-bold tracking-tight" data-testid="contact-name">
            {contact.name}
          </SheetTitle>
          {contact.company && (
            <SheetDescription className="flex items-center gap-1.5 text-stone-600">
              <Building2 className="h-3.5 w-3.5" />
              {contact.company}
            </SheetDescription>
          )}
        </SheetHeader>

        <div className="px-6 py-5 space-y-5">
          {/* Identity + License */}
          <div className="grid grid-cols-2 gap-3">
            <Field icon={IdCard} label="License No" value={contact.license_no} mono />
            <Field icon={Calendar} label="Expires" value={contact.license_expires} mono />
            <Field icon={Tag} label="Category" value={contact.license_category} />
            <Field icon={Hash} label="Source" value={contact.source} mono />
          </div>

          {/* Contact info */}
          <div className="grid grid-cols-2 gap-3 border-t border-stone-200 pt-4">
            <EditableField
              icon={Mail}
              label="Email"
              value={contact.email}
              mono
              editable={canEnrich}
              inputType="email"
              placeholder="name@example.com"
              testIdPrefix="edit-email"
              onSave={(v) => saveField("email", v)}
              onEditingChange={(on) => setEditingCount((n) => n + (on ? 1 : -1))}
            />
            <EditableField
              icon={Phone}
              label="Phone"
              value={contact.phone}
              mono
              editable={canEnrich}
              inputType="tel"
              placeholder="555-123-4567"
              testIdPrefix="edit-phone"
              onSave={(v) => saveField("phone", v)}
              onEditingChange={(on) => setEditingCount((n) => n + (on ? 1 : -1))}
            />
          </div>

          {/* Location */}
          <div className="border-t border-stone-200 pt-4 space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <Field icon={MapPin} label="Licensed In" value={contact.licensed_state} mono />
              <Field icon={MapPin} label="Mailing State" value={contact.mailing_state} mono />
            </div>
            <Field icon={MapPin} label="County" value={contact.county} />
            <Field
              icon={MapPin}
              label="Mailing Address"
              value={[contact.mailing_address, contact.mailing_city, contact.mailing_state, contact.mailing_zip]
                .filter(Boolean).join(", ") || null}
            />
          </div>

          {/* Workflow */}
          <div className="border-t border-stone-200 pt-4">
            <Field
              label="Last contacted"
              value={contact.last_contacted_at ? formatTime(contact.last_contacted_at) : null}
              mono
            />
            <Field
              label="Assigned to"
              value={contact.assigned_to_name ? (mine ? `${contact.assigned_to_name} (you)` : contact.assigned_to_name) : "Unassigned"}
            />
          </div>

          {/* Actions */}
          <div className="border-t border-stone-200 pt-4 space-y-2.5">
            {!locked && !contact.assigned_to && (
              <Button
                onClick={() => onClaim(contact.id)}
                className="w-full rounded-sm bg-[#B85C38] hover:bg-[#9A4D2E] text-white h-10"
                data-testid="sheet-claim-btn"
              >
                <Hand className="h-4 w-4 mr-1.5" /> Claim this contact
              </Button>
            )}
            {!locked && mine && (
              <Button
                onClick={() => onContacted(contact.id)}
                variant="outline"
                className="w-full rounded-sm border-[#4A5D23] text-[#4A5D23] hover:bg-[#4A5D23] hover:text-white h-10"
                data-testid="sheet-contacted-btn"
              >
                <CheckCircle2 className="h-4 w-4 mr-1.5" /> Mark as Contacted & Lock
              </Button>
            )}
            {!locked && !mine && contact.assigned_to && isAdmin && (
              <div
                className="w-full rounded-sm border border-stone-300 bg-stone-50 px-3 py-2.5 text-xs text-stone-700"
                data-testid="sheet-admin-claim-hint"
              >
                <span className="font-mono uppercase tracking-wider text-stone-500 mr-1.5">
                  Claimed by {contact.assigned_to_name || "another user"}
                </span>
                — reassign to yourself below before you can mark it contacted.
              </div>
            )}

            {isAdmin && !locked && (
              <div className="border border-stone-200 p-3 rounded-sm bg-stone-50">
                <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-stone-600 mb-2">
                  Admin · Reassign
                </div>
                <div className="flex gap-2">
                  <Select value={reassignTo} onValueChange={setReassignTo}>
                    <SelectTrigger className="flex-1 h-9 rounded-sm border-stone-300" data-testid="reassign-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="unassigned">Unassign</SelectItem>
                      {users.map((u) => (
                        <SelectItem key={u.id} value={u.id}>
                          {u.name} ({u.role}{u.assigned_state ? ` · ${u.assigned_state}` : ""})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button
                    size="sm"
                    onClick={() => onReassign(contact.id, reassignTo === "unassigned" ? null : reassignTo)}
                    className="rounded-sm bg-[#1C1917] hover:bg-[#292524] text-white h-9"
                    data-testid="reassign-apply-btn"
                  >
                    Apply
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* Reminder */}
          <div className="border-t border-stone-200 pt-4">
            <ReminderPanel contactId={contact.id} />
          </div>

          {/* Notes */}
          <NotesPanel contactId={contact.id} currentUser={currentUser} />

          {/* Activity */}
          <div className="border-t border-stone-200 pt-4">
            <div className="flex items-center gap-2 mb-3">
              <Activity className="h-4 w-4 text-[#B85C38]" />
              <h3 className="font-display font-semibold tracking-tight">Activity</h3>
              <span className="text-[10px] font-mono uppercase tracking-wider text-stone-500 ml-auto">
                {activity.length} {activity.length === 1 ? "entry" : "entries"}
              </span>
            </div>
            {loadingActivity ? (
              <div className="space-y-2">
                {[...Array(3)].map((_, i) => <div key={i} className="h-10 shimmer" />)}
              </div>
            ) : activity.length === 0 ? (
              <div className="text-sm text-stone-500 italic">No activity yet.</div>
            ) : (
              <ol className="space-y-3" data-testid="activity-list">
                {activity.map((a) => {
                  const color = ACTION_COLORS[a.action] || "#57534E";
                  return (
                    <li key={a.id} className="flex gap-3">
                      <div className="flex flex-col items-center pt-1">
                        <div className="h-2.5 w-2.5 rounded-full ring-4 ring-white" style={{ backgroundColor: color }} />
                        <div className="flex-1 w-px bg-stone-200 mt-1" />
                      </div>
                      <div className="flex-1 pb-3 border-b border-stone-100 last:border-b-0">
                        <div className="flex items-baseline justify-between gap-3">
                          <span className="text-sm font-medium" style={{ color }}>
                            {ACTION_LABELS[a.action] || a.action}
                          </span>
                          <span className="text-[10px] font-mono text-stone-500 whitespace-nowrap">
                            {formatTime(a.timestamp)}
                          </span>
                        </div>
                        <div className="text-xs text-stone-600 mt-0.5">
                          by <span className="font-medium">{a.user_name || a.user_email}</span>
                          {a.meta?.to ? <> → <span className="font-medium">{a.meta.to}</span></> : null}
                          {a.meta?.fields?.length ? (
                            <> · changed: <span className="font-mono">{a.meta.fields.join(", ")}</span></>
                          ) : null}
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ol>
            )}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

function Field({ icon: Icon, label, value, mono = false }) {
  if (!value) return null;
  return (
    <div>
      <div className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-[0.2em] text-stone-500">
        {Icon && <Icon className="h-3 w-3" />}
        {label}
      </div>
      <div className={`text-sm text-stone-900 mt-0.5 break-words ${mono ? "font-mono" : ""}`}>
        {value}
      </div>
    </div>
  );
}

function EditableField({
  icon: Icon, label, value, mono = false, editable, inputType = "text",
  placeholder, testIdPrefix, onSave, onEditingChange,
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || "");
  const [busy, setBusy] = useState(false);

  const enterEdit = () => {
    setDraft(value || "");
    setEditing(true);
    onEditingChange?.(true);
  };
  const exitEdit = () => {
    setEditing(false);
    onEditingChange?.(false);
  };
  const start = enterEdit;
  const cancel = () => { setDraft(value || ""); exitEdit(); };
  const commit = async () => {
    const trimmed = draft.trim();
    if (trimmed === (value || "")) { exitEdit(); return; }
    setBusy(true);
    try {
      await onSave(trimmed);
      exitEdit();
    } catch { /* keep editing on failure so user can retry */ }
    finally { setBusy(false); }
  };

  return (
    <div className="group" data-testid={`${testIdPrefix}-field`}>
      <div className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-[0.2em] text-stone-500">
        {Icon && <Icon className="h-3 w-3" />}
        {label}
        {editable && !editing && (
          <button
            onClick={start}
            className="ml-auto opacity-0 group-hover:opacity-100 transition-opacity text-stone-500 hover:text-[#B85C38]"
            title={value ? `Edit ${label}` : `Add ${label}`}
            data-testid={`${testIdPrefix}-edit-btn`}
          >
            <Pencil className="h-3 w-3" />
          </button>
        )}
      </div>
      {editing ? (
        <div className="mt-1 flex items-center gap-1.5" data-testid={`${testIdPrefix}-editor`}>
          <Input
            autoFocus
            type={inputType}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") { e.preventDefault(); e.nativeEvent.stopImmediatePropagation(); commit(); }
              if (e.key === "Escape") { e.preventDefault(); e.nativeEvent.stopImmediatePropagation(); cancel(); }
            }}
            placeholder={placeholder}
            className={`h-8 rounded-sm border-stone-300 text-sm ${mono ? "font-mono" : ""}`}
            disabled={busy}
            data-testid={`${testIdPrefix}-input`}
          />
          <button
            onClick={commit}
            disabled={busy}
            className="h-8 w-8 grid place-items-center bg-[#B85C38] hover:bg-[#9A4D2E] text-white rounded-sm disabled:opacity-50"
            title="Save"
            data-testid={`${testIdPrefix}-save-btn`}
          >
            <Check className="h-4 w-4" />
          </button>
          <button
            onClick={cancel}
            disabled={busy}
            className="h-8 w-8 grid place-items-center border border-stone-300 hover:bg-stone-100 rounded-sm disabled:opacity-50"
            title="Cancel"
            data-testid={`${testIdPrefix}-cancel-btn`}
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      ) : (
        <div
          className={`text-sm mt-0.5 break-words ${mono ? "font-mono" : ""} ${value ? "text-stone-900" : "text-stone-400 italic"}`}
          data-testid={`${testIdPrefix}-value`}
        >
          {value || (editable ? "— add" : "—")}
        </div>
      )}
    </div>
  );
}
